import {
  collection,
  deleteField,
  doc,
  type Firestore,
  getDoc,
  getDocs,
  query,
  setDoc,
  where,
} from 'firebase/firestore';
import { v4 } from 'uuid';
import { copyMissingMeals, shiftWeek } from '@/service/weekplan/history';
import {
  DAYS,
  type Day,
  type DayMeals,
  type MealEntry,
  type Slot,
  readWeekPlan,
  type WeekPlan,
} from '@/service/weekplan/types';

export function getWeekStart(
  weekStartDay: Day,
  now: Date = new Date(),
): string {
  const dayIndex = DAYS.indexOf(weekStartDay);
  const currentDay = (now.getDay() + 6) % 7; // Monday = 0, Sunday = 6
  let diff = currentDay - dayIndex;
  if (diff < 0) diff += 7;
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - diff);
  weekStart.setHours(0, 0, 0, 0);
  return weekStart.toISOString().split('T')[0];
}

export default class WeekPlanClient {
  constructor(
    private readonly firestore: Firestore,
    private readonly uid: string,
  ) {}

  async getCurrentPlan(weekStartDay: Day): Promise<WeekPlan | undefined> {
    const weekStart = getWeekStart(weekStartDay);
    const q = query(
      collection(this.firestore, 'weekplans'),
      where('weekStart', '==', weekStart),
      where('user', '==', this.uid),
    );
    const snapshot = await getDocs(q);
    if (snapshot.empty) return undefined;
    return readWeekPlan(snapshot.docs[0].data());
  }

  async createPlan(weekStartDay: Day): Promise<WeekPlan> {
    const plan: WeekPlan = {
      id: v4(),
      user: this.uid,
      weekStart: getWeekStart(weekStartDay),
      meals: {},
    };
    await setDoc(doc(this.firestore, 'weekplans', plan.id), plan);
    return plan;
  }

  async getOrCreatePlan(weekStartDay: Day): Promise<WeekPlan> {
    const existing = await this.getCurrentPlan(weekStartDay);
    if (existing) return existing;
    return this.createPlan(weekStartDay);
  }

  // This user's plan for the week, or an unsaved one: browsing weeks does
  // not create documents, the first change does (see writeDays)
  async planFor(weekStart: string): Promise<WeekPlan> {
    const snapshot = await getDocs(
      query(
        collection(this.firestore, 'weekplans'),
        where('weekStart', '==', weekStart),
        where('user', '==', this.uid),
      ),
    );
    if (!snapshot.empty) return readWeekPlan(snapshot.docs[0].data());
    return { id: v4(), user: this.uid, weekStart, meals: {} };
  }

  // This user's plans of the weeks since `weekStart` (filtered here, so no
  // composite index is needed)
  async recentPlans(sinceWeekStart: string): Promise<WeekPlan[]> {
    const snapshot = await getDocs(
      query(
        collection(this.firestore, 'weekplans'),
        where('user', '==', this.uid),
      ),
    );
    return snapshot.docs
      .map((plan) => readWeekPlan(plan.data()))
      .filter(({ weekStart }) => weekStart >= sinceWeekStart);
  }

  // Fills the empty slots from the previous week; returns the plan and the
  // number of meals copied
  async copyFromPreviousWeek(
    plan: WeekPlan,
  ): Promise<{ plan: WeekPlan; copied: number }> {
    const previous = await this.planFor(shiftWeek(plan.weekStart, -1));
    const { days, copied } = copyMissingMeals(plan, previous);
    return {
      plan: copied ? await this.writeDays(plan, days) : plan,
      copied,
    };
  }

  // Adds accepted meals (an AI proposal) to the empty slots; returns the
  // plan and the number of meals added
  async addMeals(
    plan: WeekPlan,
    meals: WeekPlan['meals'],
  ): Promise<{ plan: WeekPlan; added: number }> {
    const { days, copied } = copyMissingMeals(plan, { ...plan, meals });
    return {
      plan: copied ? await this.writeDays(plan, days) : plan,
      added: copied,
    };
  }

  async getPlan(id: string): Promise<WeekPlan | undefined> {
    const snapshot = await getDoc(doc(this.firestore, 'weekplans', id));
    if (snapshot.exists()) {
      return readWeekPlan(snapshot.data());
    }
    return undefined;
  }

  // Returns the plan with the meal set
  async setMeal(
    plan: WeekPlan,
    day: Day,
    slot: Slot,
    entry: MealEntry,
  ): Promise<WeekPlan> {
    return this.writeDay(plan, day, { ...plan.meals[day], [slot]: entry });
  }

  // Returns the plan without the meal
  async removeMeal(plan: WeekPlan, day: Day, slot: Slot): Promise<WeekPlan> {
    const { [slot]: _, ...rest } = plan.meals[day] ?? {};
    return this.writeDay(plan, day, rest);
  }

  private async writeDay(
    plan: WeekPlan,
    day: Day,
    meals: DayMeals,
  ): Promise<WeekPlan> {
    return this.writeDays(plan, { [day]: meals });
  }

  // Writes whole days, which also moves a day stored before meal slots to
  // the new shape, and creates the plan on its first change. Firestore
  // rejects undefined, so empty slots and unset servings are left out.
  private async writeDays(
    plan: WeekPlan,
    days: Partial<Record<string, DayMeals>>,
  ): Promise<WeekPlan> {
    const meals = { ...plan.meals };
    const data: Record<string, unknown> = {
      id: plan.id,
      user: plan.user ?? this.uid,
      weekStart: plan.weekStart,
    };
    for (const [day, dayMeals] of Object.entries(days)) {
      const filled = Object.fromEntries(
        Object.entries(dayMeals ?? {})
          .filter(([, entry]) => entry)
          .map(([slot, entry]) => [
            slot,
            Object.fromEntries(
              Object.entries(entry!).filter(([, value]) => value !== undefined),
            ),
          ]),
      ) as DayMeals;
      if (Object.keys(filled).length) {
        data[`meals.${day}`] = filled;
        meals[day] = filled;
      } else {
        data[`meals.${day}`] = deleteField();
        delete meals[day];
      }
    }
    // Dotted keys are field paths for mergeFields, but plain keys in data:
    // nest them so setDoc writes meals.<day>
    const nested: Record<string, unknown> = { ...data };
    const nestedMeals: Record<string, unknown> = {};
    for (const key of Object.keys(data)) {
      if (key.startsWith('meals.')) {
        nestedMeals[key.slice('meals.'.length)] = data[key];
        delete nested[key];
      }
    }
    nested.meals = nestedMeals;
    await setDoc(doc(this.firestore, 'weekplans', plan.id), nested, {
      mergeFields: Object.keys(data),
    });
    return { ...plan, user: plan.user ?? this.uid, meals };
  }
}
